import { useEffect, useMemo, useState } from 'react'
import { useAuth } from './auth-oidc'
import type { AccountStatus } from '@/lib/api'
import { useAccountStatuses, useNextFreeTimes } from '@/lib/api'

// Next-free-time state for an account
export interface NextFreeState {
  hasSubscription: boolean
  isFree: boolean
  nextFreeTime?: Date // undefined = no sub or >3h away
}

// UI Account interface for components
export interface UIAccount {
  id: string // account_name from API
  name: string // account_name formatted, or custom name
  email?: string // Not provided by API, but used by UI
  hasToken?: boolean // Has valid Communauto token
  hasActiveReservation?: boolean
  activeReservationCar?: string
  lastTripEnd?: Date
  nextFree?: NextFreeState
  status?: 'idle' | 'reserved' | 'ongoing-trip'
  hasUpcomingReservation?: boolean
  reservationCar?: string
  reservationStartTime?: Date
  reservationEndTime?: Date
  reservationRemainingTime?: number
  hasOngoingTrip?: boolean
  tripCar?: string
  tripStartTime?: Date
  tripDuration?: number
  vehicleLocation?: { latitude: number; longitude: number }
}

// Transform API AccountStatus to UI format
function transformAccountStatus(
  accountName: string,
  apiStatus: AccountStatus | null,
  nextFreeData?: {
    has_subscription: boolean
    is_free: boolean
    next_free_time?: string | null
  },
): UIAccount {
  const account: UIAccount = {
    id: accountName,
    name: accountName, // Use account_name as display name
  }

  // If apiStatus is null (query failed), return account with minimal info
  if (!apiStatus) {
    account.status = 'idle'
    account.hasActiveReservation = false
    account.hasToken = false
    return account
  }

  // Pass through has_token from API
  account.hasToken = apiStatus.has_token

  // Determine status from rental state or account status
  if (apiStatus.rental_details) {
    const rental = apiStatus.rental_details
    const hasUsedStartDate = !!rental.usedStartDate

    // Copy vehicle location if available
    account.vehicleLocation = rental.vehicle.vehicleLocation ?? undefined

    // If usedStartDate exists, trip has started
    if (hasUsedStartDate) {
      account.status = 'ongoing-trip'
      account.hasOngoingTrip = true
      account.tripCar =
        rental.vehicle.model || `Vehicle #${rental.vehicle.vehicleNb}`

      if (rental.usedStartDate) {
        account.tripStartTime = new Date(rental.usedStartDate)
        account.tripDuration = Math.floor(
          (Date.now() - account.tripStartTime.getTime()) / 60000,
        )
      }

      // Add reservation end time for ongoing trips
      if (rental.reservedEndDate) {
        account.reservationEndTime = new Date(rental.reservedEndDate)
      }
    } else {
      // Reserved but not yet started
      account.status = 'reserved'
      account.hasActiveReservation = true
      account.hasUpcomingReservation = true
      account.reservationCar =
        rental.vehicle.model || `Vehicle #${rental.vehicle.vehicleNb}`
      account.activeReservationCar = account.reservationCar

      if (rental.reservedStartDate) {
        account.reservationStartTime = new Date(rental.reservedStartDate)
        const now = Date.now()
        const startTime = account.reservationStartTime.getTime()
        if (startTime > now) {
          account.reservationRemainingTime = Math.floor(
            (startTime - now) / 60000,
          )
        }
      }

      // Add reservation end time
      if (rental.reservedEndDate) {
        account.reservationEndTime = new Date(rental.reservedEndDate)
      }
    }
  } else {
    // No rental = idle
    account.status = 'idle'
    account.hasActiveReservation = false
  }

  // Map last_end_trip to lastTripEnd
  if (apiStatus.last_end_trip) {
    account.lastTripEnd = new Date(apiStatus.last_end_trip)
  }

  // Map next-free-time data
  if (nextFreeData) {
    account.nextFree = {
      hasSubscription: nextFreeData.has_subscription,
      isFree: nextFreeData.is_free,
      nextFreeTime: nextFreeData.next_free_time
        ? new Date(nextFreeData.next_free_time)
        : undefined,
    }
  }

  return account
}

// Hook to fetch and manage accounts using TanStack Query
export function useAccounts() {
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth()
  const [isMounted, setIsMounted] = useState(false)

  // Track if component has mounted on client (prevents SSR hydration mismatch)
  useEffect(() => {
    setIsMounted(true)
  }, [])

  const accountNames = useMemo(
    () => (isAuthenticated && user?.accessible_accounts) || [],
    [isAuthenticated, user?.accessible_accounts],
  )

  // Only fetch account statuses after client has mounted
  const shouldFetch = isMounted && accountNames.length > 0

  const {
    data: accountStatuses,
    isLoading,
    isPending,
    error,
    refetch,
    dataUpdatedAt,
  } = useAccountStatuses(accountNames, shouldFetch)

  // Fetch next-free-time data for all accounts in parallel
  const { data: nextFreeTimes } = useNextFreeTimes(accountNames, shouldFetch)

  const accounts = useMemo(() => {
    if (accountStatuses.length === 0) return []
    return accountStatuses.map((item) => {
      const nft = nextFreeTimes.find((n) => n.accountName === item.accountName)
      return transformAccountStatus(
        item.accountName,
        item.status,
        nft?.nextFree ?? undefined,
      )
    })
  }, [accountStatuses, nextFreeTimes])

  return {
    accounts,
    accountStatuses, // Expose raw statuses for single account lookup
    isLoading: !isMounted || isLoading || isPending || isAuthLoading,
    error,
    refresh: refetch,
    dataUpdatedAt, // Timestamp of when data was last fetched
  }
}

// Hook to get a single account by ID/name
export function useAccount(accountId: string | null | undefined) {
  const {
    accounts,
    accountStatuses,
    isLoading,
    error,
    refresh,
    dataUpdatedAt,
  } = useAccounts()
  const { user } = useAuth()

  // Find transformed UIAccount
  const account = accountId
    ? accounts.find((acc) => acc.id === accountId)
    : null

  // Find raw API status for this account (single source of truth)
  const accountStatus = accountId
    ? (accountStatuses.find((s) => s.accountName === accountId)?.status ?? null)
    : null

  // Check if this account is in the user's accessible accounts list
  const accessibleAccounts = user?.accessible_accounts ?? []
  const isAccessible = accountId
    ? accessibleAccounts.includes(accountId)
    : false

  // Keep loading while:
  // 1. useAccounts is still loading (includes isPending and isAuthLoading), OR
  // 2. We have no accounts loaded yet (prevents showing "not found" during initial race condition), OR
  // 3. The account is in accessible list but hasn't loaded yet (prevents blank page during race condition)
  const effectivelyLoading =
    isLoading ||
    (accounts.length === 0 && !error) ||
    (isAccessible && !account && !error)

  // Only show "not found" if:
  // - Loading is complete
  // - We have at least one account loaded (proves queries ran)
  // - The specific account wasn't found
  // - AND it's NOT in the accessible accounts list (prevents showing error for accounts that are just slow to load)
  const isNotFound =
    !effectivelyLoading &&
    accounts.length > 0 &&
    !account &&
    !!accountId &&
    !isAccessible

  return {
    account,
    accountStatus, // Raw API response for this account
    isLoading: effectivelyLoading,
    error,
    isNotFound,
    refresh,
    dataUpdatedAt, // Timestamp of when data was last fetched
  }
}
